import axios from 'axios';
import { API_ENDPOINTS } from './types';

// Error message parser utility
function parseErrorMessage(error: unknown, defaultMessage: string): string {
  if (axios.isAxiosError(error)) {
    // Check for backend error response
    if (error.response?.data) {
      const data = error.response.data;
      if (typeof data === 'string') return data;
      if (data.message) return data.message;
      if (data.error) return data.error;
    }
    // Check for network errors
    if (error.code === 'ECONNABORTED') return 'Request timed out. Please try again.';
    if (!error.response) return 'Network error. Please check your connection.';
  }
  if (error instanceof Error) return error.message;
  return defaultMessage;
}

// ========================================
// Pagination Types
// ========================================
export interface PagedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

// PageResponse matches the backend's com.enveu.flow.commons.dto.core.PageResponse
export interface PageResponse<T> {
  contents: T[];
  totalElements: number;
  totalPages: number;
  pageSize: number;
  pageNumber: number;
}

// ========================================
// Credit Management DTOs
// ========================================
export interface TenantCreditBalanceDTO {
  tenantId: string;
  tenantName: string;
  currentBalance: number;
  totalAllocated: number;
  totalDeducted: number;
  totalUsed: number;
  lastTransactionAt: string | null;
}

export interface CreditTransactionDTO {
  id: number;
  tenantId: string;
  tenantName: string;
  transactionType: 'ALLOCATION' | 'DEDUCTION' | 'USAGE' | 'ADJUSTMENT' | 'REFUND';
  category: 'GLOBAL';
  amount: number;
  balanceAfter: number;
  description: string | null;
  referenceId: string | null;
  performedBy: string;
  performedByEmail: string | null;
  createdAt: string;
  expiresAt: string | null;
}

export interface CreditTransactionRequest {
  tenantId: string;
  amount: number;
  transactionType: 'ALLOCATION' | 'DEDUCTION';
  category?: 'GLOBAL';
  description?: string;
  referenceId?: string;
}

export interface CreditStatsDTO {
  totalAllocated: number;
  totalUsed: number;
  totalAvailable: number;
  lowBalanceCount: number;
  tenantCount: number;
}

// ========================================
// Credit Grant Types
// ========================================
export type CreditGrantSource = 
  | 'PLAN_RENEWAL'
  | 'PROMO'
  | 'MANUAL'
  | 'UPGRADE_DELTA'
  | 'REFUND'
  | 'PURCHASE'
  | 'TRANSFER'
  | 'SIGNUP_BONUS'
  | 'REFERRAL'
  | 'COMPENSATION';

export const CREDIT_GRANT_SOURCE_LABELS: Record<CreditGrantSource, string> = {
  PLAN_RENEWAL: 'Plan Renewal',
  PROMO: 'Promotional',
  MANUAL: 'Manual Allocation',
  UPGRADE_DELTA: 'Plan Upgrade',
  REFUND: 'Refund',
  PURCHASE: 'Purchase',
  TRANSFER: 'Transfer',
  SIGNUP_BONUS: 'Signup Bonus',
  REFERRAL: 'Referral',
  COMPENSATION: 'Compensation',
};

export interface TenantCreditGrantDTO {
  id: number;
  tenantId: string;
  tenantName: string;
  meteringKey: string;
  grantedCredits: number;
  consumedCredits: number;
  reservedCredits: number;
  remainingCredits: number;
  source: CreditGrantSource;
  referenceId: string | null;
  description: string | null;
  expiresAt: string | null;
  effectiveFrom: string | null;
  isActive: boolean;
  priority: number;
  createdBy: string | null;
  createdByEmail: string | null;
  createdAt: string;
  lastUpdated: string;
}

export interface CreditGrantRequest {
  tenantId: string;
  amount: number;
  source: CreditGrantSource;
  meteringKey?: string;
  description?: string;
  referenceId?: string;
  expiresAt?: string;
  effectiveFrom?: string;
  priority?: number;
}

export interface TenantWalletDTO {
  id: number;
  tenantId: string;
  tenantName: string;
  meteringKey: string;
  availableCredits: number;
  reservedCredits: number;
  consumedCredits: number;
  totalCredits: number;
  currency: string;
  createdAt: string;
  lastUpdated: string;
}

// ========================================
// Billing Parameter Types
// ========================================
export type BillingParameterType = 'CONSUMABLE' | 'QUOTA' | 'FEATURE';
export type DeductionTiming = 'ON_WORKFLOW_START' | 'ON_COMPLETION' | 'ON_NODE_EXECUTION' | 'REAL_TIME';
export type BillingWindow = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY' | 'LIFETIME';
export type MeasurementType = 'INSTANTANEOUS' | 'CUMULATIVE';
export type EnforcementType = 'HARD_BLOCK' | 'SOFT_WARNING' | 'TRACKING_ONLY';

// Metering keys - stable system identifiers for what to measure
export type MeteringKey = 
  // Workflow-related
  | 'WORKFLOW_EXECUTIONS_TOTAL'
  | 'WORKFLOW_CONCURRENCY_MAX'
  | 'WORKFLOW_EXECUTION_TIME_MAX'
  // Workspace-related
  | 'WORKSPACES_MAX'
  // Logs
  | 'LOG_RETENTION_DAYS_MAX'
  // Managed actions
  | 'MANAGED_ACTION_RUNTIME_MS'
  // Future
  | 'API_CALLS_TOTAL'
  | 'AI_TOKENS_TOTAL'
  | 'DATA_PROCESSED_GB'
  | 'TEAM_MEMBERS_MAX'
  | 'STORAGE_GB_MAX';

// Grouped metering keys for UI display
export const METERING_KEY_GROUPS = {
  'Workflow': [
    { value: 'WORKFLOW_EXECUTIONS_TOTAL', label: 'Total Workflow Executions' },
    { value: 'WORKFLOW_CONCURRENCY_MAX', label: 'Max Concurrent Executions' },
    { value: 'WORKFLOW_EXECUTION_TIME_MAX', label: 'Max Execution Time' },
  ],
  'Workspace': [
    { value: 'WORKSPACES_MAX', label: 'Max Workspaces' },
  ],
  'Logs & Retention': [
    { value: 'LOG_RETENTION_DAYS_MAX', label: 'Max Log Retention Days' },
  ],
  'Managed Actions': [
    { value: 'MANAGED_ACTION_RUNTIME_MS', label: 'Managed Action Runtime (seconds)' },
  ],
  // 'API & AI (Future)': [
  //   { value: 'API_CALLS_TOTAL', label: 'Total API Calls' },
  //   { value: 'AI_TOKENS_TOTAL', label: 'Total AI Tokens' },
  // ],
  // 'Storage (Future)': [
  //   { value: 'DATA_PROCESSED_GB', label: 'Data Processed (GB)' },
  //   { value: 'STORAGE_GB_MAX', label: 'Max Storage (GB)' },
  // ],
  // 'Team (Future)': [
  //   { value: 'TEAM_MEMBERS_MAX', label: 'Max Team Members' },
  // ],
} as const;

export interface BillingParameterDTO {
  id: number;
  code: string;
  name: string;
  description: string | null;
  type: BillingParameterType;
  unit: string;
  // CONSUMABLE specific
  creditCost: number | null;
  deductionTiming: DeductionTiming | null;
  billingWindow: BillingWindow | null;
  // CONSUMABLE / QUOTA specific
  meteringKey: MeteringKey | null;  // System metering key for metered CONSUMABLE and QUOTA params
  measurementType: MeasurementType | null;
  enforcementType: EnforcementType | null;
  // Common
  active: boolean;
  displayOrder: number | null;
  metadata: string | null;
  featureCode: string | null;  // Optional parent FEATURE parameter code (QUOTA/CONSUMABLE only)
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
}

export interface BillingParameterCreateRequest {
  code: string;
  name: string;
  description?: string;
  type: BillingParameterType;
  unit: string;
  // CONSUMABLE specific
  creditCost?: number;
  deductionTiming?: DeductionTiming;
  billingWindow?: BillingWindow;
  // CONSUMABLE / QUOTA specific
  meteringKey?: MeteringKey;  // Required for metered CONSUMABLE and QUOTA params
  measurementType?: MeasurementType;
  enforcementType?: EnforcementType;
  displayOrder?: number;
  metadata?: string;
  featureCode?: string;  // Optional parent FEATURE parameter code (QUOTA/CONSUMABLE only)
}

export interface BillingParameterUpdateRequest {
  name?: string;
  description?: string;
  // CONSUMABLE specific
  creditCost?: number;
  deductionTiming?: DeductionTiming;
  billingWindow?: BillingWindow;
  // CONSUMABLE / QUOTA specific
  meteringKey?: MeteringKey;  // Can update metering key for metered CONSUMABLE and QUOTA params
  measurementType?: MeasurementType;
  enforcementType?: EnforcementType;
  // Common
  active?: boolean;
  displayOrder?: number;
  metadata?: string;
  featureCode?: string;  // Optional parent FEATURE parameter code (QUOTA/CONSUMABLE only)
}

export interface BillingParameterStatsDTO {
  totalCount: number;
  consumableCount: number;
  quotaCount: number;
  featureCount: number;
  activeCount: number;
  inactiveCount: number;
}

// ========================================
// Plan Types - Binding layer for billing parameters
// ========================================

export type PlanBillingCycle = 'MONTHLY' | 'YEARLY' | 'LIFETIME' | 'CUSTOM';
export type PlanStatus = 'DRAFT' | 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
export type StripeSyncStatus = 'NOT_SYNCED' | 'SYNCING' | 'SYNCED' | 'SYNC_FAILED' | 'OUT_OF_SYNC';
export type OverageMode = 'PAY_PER_USE' | 'BLOCK' | 'SOFT_LIMIT';
export type PaymentProvider = 'NONE' | 'STRIPE' | 'MANUAL' | 'AWS_MARKETPLACE';

export const PAYMENT_PROVIDER_LABELS: Record<PaymentProvider, string> = {
  NONE: 'Free',
  STRIPE: 'Stripe',
  MANUAL: 'Manual',
  AWS_MARKETPLACE: 'AWS Marketplace',
};

// Configuration for a billing parameter within a plan
export interface PlanParameterConfigDTO {
  id: number;
  planId: number;
  parameterId: number;
  // Parameter details (populated from BillingParameter)
  parameterCode: string;
  parameterName: string;
  parameterType: BillingParameterType;
  parameterUnit: string;
  parameterCreditCost: number | null; // Credit cost per unit (for CONSUMABLE)
  // Configuration values
  includedValue: number | null;       // For consumables: included quantity
  limitValue: number | null;          // For quotas: the limit value
  resetOnCycle: boolean;              // Whether to reset on billing cycle (consumables)
  // Feature configuration
  featureEnabled: boolean;            // For features: whether the capability is enabled on this plan
  // Overage configuration (consumables only)
  overageEnabled: boolean;
  overageMode: OverageMode | null;
  overagePricePerUnit: number | null; // Price per unit for overage
  overageMaxUnits: number | null;     // Max overage units allowed (null = unlimited)
  // Metadata
  displayOrder: number | null;
  metadata: string | null;
  createdAt: string;
  updatedAt: string;
}

// Plan DTO - the subscription plan definition
export interface PlanDTO {
  id: number;
  code: string;
  name: string;
  description: string | null;
  // Pricing
  basePrice: number;
  billingCycle: PlanBillingCycle;
  customCycleDays: number | null;     // Only for CUSTOM cycle
  currency: string;
  // Status
  status: PlanStatus;
  isPublic: boolean;                  // Whether plan is visible to customers
  isDefault: boolean;                 // Whether this plan is auto-assigned to new signups
  paymentProvider: PaymentProvider;   // Billing/payment routing for this plan
  // Quota summary (for quick display)
  totalIncludedCredits: number;       // Sum of all consumable included values * credit costs
  // Parameter configurations
  parameterConfigs: PlanParameterConfigDTO[];
  // Metadata
  displayOrder: number | null;
  metadata: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
}

// Request to create a new plan
export interface PlanCreateRequest {
  code: string;
  name: string;
  description?: string;
  basePrice: number;
  billingCycle: PlanBillingCycle;
  customCycleDays?: number;
  currency?: string;
  isPublic?: boolean;
  isDefault?: boolean;
  paymentProvider?: PaymentProvider;
  displayOrder?: number;
  metadata?: string;
  // Parameter configurations to add
  parameterConfigs?: PlanParameterConfigCreateRequest[];
}

// Request to create parameter config within a plan
export interface PlanParameterConfigCreateRequest {
  parameterId: number;
  includedValue?: number;
  limitValue?: number;
  resetOnCycle?: boolean;
  featureEnabled?: boolean;   // For FEATURE parameters: whether the capability is enabled
  overageEnabled?: boolean;
  overageMode?: OverageMode;
  overagePricePerUnit?: number;
  overageMaxUnits?: number;
  displayOrder?: number;
  metadata?: string;
}

// Request to update an existing plan
export interface PlanUpdateRequest {
  name?: string;
  description?: string;
  basePrice?: number;
  billingCycle?: PlanBillingCycle;
  customCycleDays?: number;
  currency?: string;
  status?: PlanStatus;
  isPublic?: boolean;
  isDefault?: boolean;
  paymentProvider?: PaymentProvider;
  displayOrder?: number;
  metadata?: string;
}

// Request to update a parameter config within a plan
export interface PlanParameterConfigUpdateRequest {
  includedValue?: number;
  limitValue?: number;
  resetOnCycle?: boolean;
  featureEnabled?: boolean;   // For FEATURE parameters: whether the capability is enabled
  overageEnabled?: boolean;
  overageMode?: OverageMode;
  overagePricePerUnit?: number;
  overageMaxUnits?: number;
  displayOrder?: number;
  metadata?: string;
}

// Plan statistics
export interface PlanStatsDTO {
  totalCount: number;
  activeCount: number;
  draftCount: number;
  inactiveCount: number;
  archivedCount: number;
  publicCount: number;
}

// Stripe sync result
export interface PlanStripeSyncResultDTO {
  planId: number;
  planCode: string;
  success: boolean;
  message: string;
  stripeProductId: string | null;
  stripePriceId: string | null;
  stripePriceVersion: number;
  priceChanged: boolean;
  oldPriceArchived: boolean;
  syncedAt: string;
  error: string | null;
}

// ========================================
// Normalized Billing Catalog Types (EF-259)
// BillingPlan = customer-facing tier (→ Stripe Product)
// BillingPlanPrice = purchasable option (→ Stripe Price)
// ========================================

export type SubscriptionMovementType =
  | 'TIER_UPGRADE'
  | 'TIER_DOWNGRADE'
  | 'INTERVAL_UPGRADE'
  | 'INTERVAL_DOWNGRADE'
  | 'NO_OP';

export interface BillingPlanPriceDTO {
  id: number;
  code: string;
  billingPlanId: number;
  basePrice: number;
  billingCycle: PlanBillingCycle;
  customCycleDays: number | null;
  currency: string;
  paymentProvider: PaymentProvider;
  externalPriceId: string | null;
  externalPriceVersion: number;
  providerSyncStatus: StripeSyncStatus;
  providerSyncedAt: string | null;
  providerSyncError: string | null;
  isActive: boolean;
  displayOrder: number | null;
  metadata: string | null;
  createdAt: string;
  lastUpdated: string;
}

export interface BillingPlanParameterConfigDTO {
  id: number;
  billingPlanId: number;
  parameterId: number;
  parameterCode: string;
  parameterName: string;
  parameterType: BillingParameterType;
  parameterUnit: string;
  parameterCreditCost: number | null;
  includedValue: number | null;
  limitValue: number | null;
  resetOnCycle: boolean;
  featureEnabled: boolean;
  overageEnabled: boolean;
  overageMode: OverageMode | null;
  overagePricePerUnit: number | null;
  overageMaxUnits: number | null;
  displayOrder: number | null;
  metadata: string | null;
  createdAt: string;
  lastUpdated: string;
}

export interface BillingPlanDTO {
  id: number;
  code: string;
  name: string;
  description: string | null;
  status: PlanStatus;
  isPublic: boolean;
  isDefault: boolean;
  isRecommended: boolean;
  rank: number;
  displayOrder: number | null;
  stripeProductId: string | null;
  metadata: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  lastUpdated: string;
  priceOptions: BillingPlanPriceDTO[];
  parameterConfigs: BillingPlanParameterConfigDTO[];
}

export interface BillingPlanSummaryDTO {
  id: number;
  code: string;
  name: string;
  description: string | null;
  status: PlanStatus;
  isPublic: boolean;
  isDefault: boolean;
  isRecommended: boolean;
  rank: number;
  displayOrder: number | null;
  stripeProductId: string | null;
  priceOptionCount: number;
  activePriceOptionCount: number;
  parameterCount: number;
  createdAt: string;
  lastUpdated: string;
}

export interface BillingPlanStatsDTO {
  totalTiers: number;
  activeTiers: number;
  draftTiers: number;
  inactiveTiers: number;
  archivedTiers: number;
  publicTiers: number;
  totalPriceOptions: number;
  activePriceOptions: number;
}

export interface BillingPlanCreateRequest {
  code: string;
  name: string;
  description?: string;
  rank: number;
  isPublic?: boolean;
  isDefault?: boolean;
  isRecommended?: boolean;
  displayOrder?: number;
  metadata?: string;
}

export interface BillingPlanUpdateRequest {
  name?: string;
  description?: string;
  rank?: number;
  status?: PlanStatus;
  isPublic?: boolean;
  isDefault?: boolean;
  isRecommended?: boolean;
  displayOrder?: number;
  metadata?: string;
}

export interface BillingPlanPriceCreateRequest {
  code: string;
  basePrice: number;
  billingCycle: PlanBillingCycle;
  customCycleDays?: number;
  currency?: string;
  paymentProvider?: PaymentProvider;
  externalPriceId?: string;
  providerMetadata?: string;
  isActive?: boolean;
  displayOrder?: number;
  metadata?: string;
}

export interface BillingPlanPriceUpdateRequest {
  basePrice?: number;
  isActive?: boolean;
  displayOrder?: number;
  metadata?: string;
  providerMetadata?: string;
}

export interface BillingPlanStripeSyncResultDTO {
  billingPlanId: number;
  billingPlanCode: string;
  billingPlanPriceId: number;
  billingPlanPriceCode: string;
  success: boolean;
  message: string;
  stripeProductId: string | null;
  stripePriceId: string | null;
  syncedAt: string;
  error: string | null;
}

// ========================================
// Subscription Types
// ========================================
export type SubscriptionStatus = 
  | 'ACTIVE'
  | 'TRIALING'
  | 'PAST_DUE'
  | 'CANCELED'
  | 'UNPAID'
  | 'INCOMPLETE'
  | 'INCOMPLETE_EXPIRED'
  | 'PAUSED';

export interface SubscriptionDTO {
  id: number;
  tenantId: string;
  planId: number | null;
  /** New-tier BillingPlan ID (null for legacy-plan subscriptions) */
  billingPlanId: number | null;
  /** New-tier BillingPlanPrice ID (null for legacy-plan subscriptions) */
  billingPlanPriceId: number | null;
  planName: string | null;
  planCode: string | null;
  stripeSubscriptionId: string | null;
  status: SubscriptionStatus;
  paymentProvider: PaymentProvider;   // Billing/payment routing for this subscription
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  trialStart: string | null;
  trialEnd: string | null;
  isInTrial: boolean;
  createdAt: string;
  updatedAt: string;
  // Scheduled plan change (end-of-period downgrade)
  scheduledPlanId: number | null;
  scheduledPlanName: string | null;
  scheduledPlanCode: string | null;
  scheduledChangeDate: string | null;
}

export interface CheckoutSessionRequest {
  /** Legacy plan ID. Either planId or billingPlanPriceId must be set. */
  planId?: number;
  /** New-tier BillingPlanPrice ID. Either planId or billingPlanPriceId must be set. */
  billingPlanPriceId?: number;
  successUrl: string;
  cancelUrl: string;
  trialDays?: number;
}

export interface CheckoutSessionResponse {
  checkoutSessionId: string;
  checkoutUrl: string;
  sessionExpiry: number; // Unix timestamp
}

// ========================================
// Subscription Update (Upgrade / Downgrade)
// ========================================

export interface SubscriptionUpdateRequest {
  /** Legacy plan ID. Either newPlanId or billingPlanPriceId must be set. */
  newPlanId?: number;
  /** New-tier BillingPlanPrice ID. Either newPlanId or billingPlanPriceId must be set. */
  billingPlanPriceId?: number;
  prorationBehavior?: 'always_invoice' | 'none';
  /** Required when upgrading from a free/manual plan to a Stripe-billed plan */
  successUrl?: string;
  /** Required when upgrading from a free/manual plan to a Stripe-billed plan */
  cancelUrl?: string;
}

export interface SubscriptionUpdateResponse {
  id: number;
  tenantId: string;
  planId: number;
  planName: string;
  planCode: string;
  stripeSubscriptionId: string;
  status: SubscriptionStatus;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  previousPlanId: number;
  previousPlanName: string;
  changeType: 'UPGRADED' | 'DOWNGRADED';
  prorationAmount: number | null; // in cents
  /** True when the upgrade requires 3DS/SCA authentication (EF-225) */
  requiresAction?: boolean;
  /** Stripe PaymentIntent client_secret for stripe.confirmCardPayment() (EF-225) */
  paymentClientSecret?: string | null;
  /** True when upgrading from a free/manual plan requires a Stripe Checkout redirect */
  requiresCheckout?: boolean;
  /** Stripe Checkout Session URL — present only when requiresCheckout is true */
  checkoutUrl?: string | null;
}

// Plan summary for list views (without full parameter configs)
export interface PlanSummaryDTO {
  id: number;
  code: string;
  name: string;
  description: string | null;
  basePrice: number;
  billingCycle: PlanBillingCycle;
  currency: string;
  status: PlanStatus;
  isPublic: boolean;
  isDefault: boolean;
  paymentProvider: PaymentProvider;
  parameterCount: number;
  totalIncludedCredits: number;
  stripeSyncStatus: StripeSyncStatus;
  stripeProductId: string | null;
  stripePriceId: string | null;
  stripePriceVersion: number;
  stripeSyncedAt: string | null;
  stripeSyncError: string | null;
  createdAt: string;
  updatedAt: string;
}

// ========================================
// Payment Methods & Portal
// ========================================

export interface PaymentMethodDTO {
  id: string;               // Stripe payment method ID (pm_xxx)
  type: string;             // "card", "us_bank_account", etc.
  brand: string | null;     // "visa", "mastercard", "amex", etc.
  last4: string | null;
  expiryMonth: number | null;
  expiryYear: number | null;
  holderName: string | null;
  isDefault: boolean;
  bankName: string | null;
  country: string | null;
  funding: string | null;   // "credit", "debit", "prepaid", "unknown"
}

export interface CustomerPortalSessionDTO {
  sessionId: string;
  portalUrl: string;
  returnUrl: string;
}

export type InvoiceStatus = 'DRAFT' | 'OPEN' | 'PAID' | 'VOID' | 'UNCOLLECTIBLE';

export interface SubscriptionStatsDTO {
  total: number;
  active: number;
  trialing: number;
  pastDue: number;
  canceled: number;
  paused: number;
}

export interface InvoiceStatsDTO {
  total: number;
  paid: number;
  open: number;
  draft: number;
  void: number;
  uncollectible: number;
  totalRevenueCents: number;
}

export interface InvoiceDTO {
  id: number;
  tenantId: string;
  subscriptionId: number | null;
  stripeInvoiceId: string | null;
  invoiceNumber: string | null;
  status: InvoiceStatus;
  amountDue: number;   // in cents
  amountPaid: number;  // in cents
  currency: string;
  invoicePdfUrl: string | null;
  hostedInvoiceUrl: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  dueDate: string | null;
  paidAt: string | null;
  createdAt: string;
}

// ========================================
// Usage Analytics Types
// ========================================

export interface UsageMetricDTO {
  meteringKey: string | null;
  name: string;
  type: 'CONSUMABLE' | 'QUOTA' | null;
  unit: string;
  current: number;
  limit: number | null;             // null = unlimited
  creditsConsumedPeriod: number;
  alertable: boolean;               // false for entitlement metrics (e.g. log retention) where current == limit by design
}

export interface UsageSummaryDTO {
  tenantId: string;
  planName: string | null;
  planCode: string | null;
  periodStart: string | null;       // ISO instant
  periodEnd: string | null;         // ISO instant
  creditBalance: number;
  creditsGrantedPeriod: number;
  creditsConsumedPeriod: number;
  executionsPeriod: number;
  executionLimit: number | null;    // null = unlimited
  metrics: UsageMetricDTO[];
}

export interface DailyDataPoint {
  date: string;                     // YYYY-MM-DD
  executionCount: number;
  creditsConsumed: number;
}

export interface UsageHistoryDTO {
  period: string;                   // "7d", "30d", "90d"
  periodStart: string;
  periodEnd: string;
  totalExecutions: number;
  totalCreditsConsumed: number;
  dataPoints: DailyDataPoint[];
}

// ========================================
// API Service
// ========================================

// API endpoint configuration - points to billing-svc
const BILLING_API_BASE = API_ENDPOINTS.BILLING;

/**
 * Billing API service for credits, billing parameters, and plans management
 */
class BillingApiService {
  // ========================================
  // Credit Management APIs (Admin only)
  // ========================================

  /**
   * Get credit balances for all tenants - Admin only
   */
  async adminGetAllCreditBalances(params: {
    page?: number;
    size?: number;
    search?: string;
  } = {}): Promise<PagedResponse<TenantCreditBalanceDTO>> {
    try {
      const response = await axios.get<PagedResponse<TenantCreditBalanceDTO>>(
        `${BILLING_API_BASE}/admin/credits`,
        {
          params: {
            page: params.page ?? 0,
            size: params.size ?? 20,
            search: params.search,
          },
          timeout: 15000,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get credit balances:', error);
      throw new Error(
        `Failed to get credit balances: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Get platform-wide credit statistics - Admin only
   */
  async adminGetCreditStats(): Promise<CreditStatsDTO> {
    try {
      const response = await axios.get<CreditStatsDTO>(
        `${BILLING_API_BASE}/admin/credits/stats`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get credit stats:', error);
      throw new Error(
        `Failed to get credit stats: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Get credit balance for a specific tenant - Admin only
   */
  async adminGetTenantCreditBalance(tenantId: string): Promise<TenantCreditBalanceDTO> {
    try {
      const response = await axios.get<TenantCreditBalanceDTO>(
        `${BILLING_API_BASE}/admin/credits/${encodeURIComponent(tenantId)}`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get tenant credit balance:', error);
      throw new Error(
        `Failed to get credit balance: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Get transaction history for a tenant - Admin only
   */
  async adminGetTenantTransactionHistory(
    tenantId: string,
    params: {
      page?: number;
      size?: number;
    } = {}
  ): Promise<PagedResponse<CreditTransactionDTO>> {
    try {
      const response = await axios.get<PagedResponse<CreditTransactionDTO>>(
        `${BILLING_API_BASE}/admin/credits/${encodeURIComponent(tenantId)}/transactions`,
        {
          params: {
            page: params.page ?? 0,
            size: params.size ?? 20,
          },
          timeout: 15000,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get transaction history:', error);
      throw new Error(
        `Failed to get transaction history: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Create a credit transaction (allocation or deduction) - Admin only
   * @deprecated Use adminCreateCreditGrant for new allocations
   */
  async adminCreateCreditTransaction(request: CreditTransactionRequest): Promise<CreditTransactionDTO> {
    try {
      const response = await axios.post<CreditTransactionDTO>(
        `${BILLING_API_BASE}/admin/credits/transactions`,
        request,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to create credit transaction:', error);
      throw new Error(parseErrorMessage(error, 'Failed to create credit transaction'));
    }
  }

  // ========================================
  // Credits APIs (Non-Admin) - For Tenant Admins/Owners
  // ========================================

  /**
   * Get credit balance for the current tenant - Requires TENANT_ADMIN or TENANT_OWNER role
   * @param tenantId - Optional tenant ID. If not provided, backend uses user's default tenant.
   */
  async getCreditBalance(tenantId?: string): Promise<TenantCreditBalanceDTO> {
    try {
      const response = await axios.get<TenantCreditBalanceDTO>(
        `${BILLING_API_BASE}/credits/balance`,
        { 
          params: tenantId ? { tenantId } : undefined,
          timeout: 10000 
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get credit balance:', error);
      throw new Error(
        `Failed to get credit balance: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Get transaction history for the current tenant - Requires TENANT_ADMIN or TENANT_OWNER role
   * @param tenantId - Optional tenant ID. If not provided, backend uses user's default tenant.
   * @param params - Pagination parameters
   */
  async getCreditTransactionHistory(
    tenantId?: string,
    params: {
      page?: number;
      size?: number;
    } = {}
  ): Promise<PagedResponse<CreditTransactionDTO>> {
    try {
      const response = await axios.get<PagedResponse<CreditTransactionDTO>>(
        `${BILLING_API_BASE}/credits/transactions`,
        {
          params: {
            ...(tenantId ? { tenantId } : {}),
            page: params.page ?? 0,
            size: params.size ?? 20,
          },
          timeout: 15000,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get credit transaction history:', error);
      throw new Error(
        `Failed to get transaction history: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  // ========================================
  // Org-level Credit Grants & Wallets (read-only)
  // ========================================

  /**
   * Get grants for the current tenant - Requires TENANT_ADMIN or TENANT_OWNER role
   */
  async getCreditGrants(
    tenantId?: string,
    params: { page?: number; size?: number } = {}
  ): Promise<PagedResponse<TenantCreditGrantDTO>> {
    try {
      const response = await axios.get<PagedResponse<TenantCreditGrantDTO>>(
        `${BILLING_API_BASE}/credits/grants`,
        {
          params: {
            ...(tenantId ? { tenantId } : {}),
            page: params.page ?? 0,
            size: params.size ?? 25,
          },
          timeout: 15000,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get credit grants:', error);
      throw new Error(parseErrorMessage(error, 'Failed to get credit grants'));
    }
  }

  /**
   * Get wallets for the current tenant - Requires TENANT_ADMIN or TENANT_OWNER role
   */
  async getCreditWallets(tenantId?: string): Promise<TenantWalletDTO[]> {
    try {
      const response = await axios.get<TenantWalletDTO[]>(
        `${BILLING_API_BASE}/credits/wallets`,
        {
          params: tenantId ? { tenantId } : undefined,
          timeout: 10000,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get credit wallets:', error);
      throw new Error(parseErrorMessage(error, 'Failed to get credit wallets'));
    }
  }

  // ========================================
  // Credit Grant Management - Admin only
  // ========================================

  /**
   * Create a credit grant with source tracking - Admin only
   */
  async adminCreateCreditGrant(request: CreditGrantRequest): Promise<TenantCreditGrantDTO> {
    try {
      const response = await axios.post<TenantCreditGrantDTO>(
        `${BILLING_API_BASE}/admin/credits/grants`,
        request,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to create credit grant:', error);
      throw new Error(parseErrorMessage(error, 'Failed to create credit grant'));
    }
  }

  /**
   * Get grants for a tenant - Admin only
   */
  async adminGetTenantGrants(
    tenantId: string,
    params: {
      page?: number;
      size?: number;
    } = {}
  ): Promise<PagedResponse<TenantCreditGrantDTO>> {
    try {
      const response = await axios.get<PagedResponse<TenantCreditGrantDTO>>(
        `${BILLING_API_BASE}/admin/credits/${encodeURIComponent(tenantId)}/grants`,
        {
          params: {
            page: params.page ?? 0,
            size: params.size ?? 20,
          },
          timeout: 15000,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get tenant grants:', error);
      throw new Error(parseErrorMessage(error, 'Failed to get tenant grants'));
    }
  }

  /**
   * Get a specific grant by ID - Admin only
   */
  async adminGetGrant(grantId: number): Promise<TenantCreditGrantDTO> {
    try {
      const response = await axios.get<TenantCreditGrantDTO>(
        `${BILLING_API_BASE}/admin/credits/grants/${grantId}`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get grant:', error);
      throw new Error(parseErrorMessage(error, 'Failed to get grant'));
    }
  }

  /**
   * Deactivate a grant - Admin only
   */
  async adminDeactivateGrant(grantId: number): Promise<TenantCreditGrantDTO> {
    try {
      const response = await axios.post<TenantCreditGrantDTO>(
        `${BILLING_API_BASE}/admin/credits/grants/${grantId}/deactivate`,
        {},
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to deactivate grant:', error);
      throw new Error(parseErrorMessage(error, 'Failed to deactivate grant'));
    }
  }

  // ========================================
  // Wallet Management - Admin only
  // ========================================

  /**
   * Get all wallets for a tenant - Admin only
   */
  async adminGetTenantWallets(tenantId: string): Promise<TenantWalletDTO[]> {
    try {
      const response = await axios.get<TenantWalletDTO[]>(
        `${BILLING_API_BASE}/admin/credits/${encodeURIComponent(tenantId)}/wallets`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get tenant wallets:', error);
      throw new Error(parseErrorMessage(error, 'Failed to get tenant wallets'));
    }
  }

  /**
   * Get a specific wallet by tenant and metering key - Admin only
   */
  async adminGetTenantWallet(tenantId: string, meteringKey: string): Promise<TenantWalletDTO> {
    try {
      const response = await axios.get<TenantWalletDTO>(
        `${BILLING_API_BASE}/admin/credits/${encodeURIComponent(tenantId)}/wallets/${encodeURIComponent(meteringKey)}`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get tenant wallet:', error);
      throw new Error(parseErrorMessage(error, 'Failed to get tenant wallet'));
    }
  }

  // ========================================
  // Billing Parameter Management - Admin only
  // ========================================

  /**
   * Get all billing parameters with pagination - Admin only
   */
  async adminGetBillingParameters(params: {
    page?: number;
    size?: number;
    search?: string;
    type?: string;
  } = {}): Promise<PagedResponse<BillingParameterDTO>> {
    try {
      const response = await axios.get<PagedResponse<BillingParameterDTO>>(
        `${BILLING_API_BASE}/admin/billing/parameters`,
        {
          params: {
            page: params.page ?? 0,
            size: params.size ?? 20,
            search: params.search,
            type: params.type,
          },
          timeout: 10000,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get billing parameters:', error);
      throw new Error(parseErrorMessage(error, 'Failed to get billing parameters'));
    }
  }

  /**
   * Get billing parameter statistics - Admin only
   */
  async adminGetBillingParameterStats(): Promise<BillingParameterStatsDTO> {
    try {
      const response = await axios.get<BillingParameterStatsDTO>(
        `${BILLING_API_BASE}/admin/billing/parameters/stats`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get billing parameter stats:', error);
      throw new Error(parseErrorMessage(error, 'Failed to get billing parameter stats'));
    }
  }

  /**
   * Get a specific billing parameter by ID - Admin only
   */
  async adminGetBillingParameterById(id: number): Promise<BillingParameterDTO> {
    try {
      const response = await axios.get<BillingParameterDTO>(
        `${BILLING_API_BASE}/admin/billing/parameters/${id}`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get billing parameter:', error);
      throw new Error(parseErrorMessage(error, 'Failed to get billing parameter'));
    }
  }

  /**
   * Get a specific billing parameter by code - Admin only
   */
  async adminGetBillingParameterByCode(code: string): Promise<BillingParameterDTO> {
    try {
      const response = await axios.get<BillingParameterDTO>(
        `${BILLING_API_BASE}/admin/billing/parameters/code/${encodeURIComponent(code)}`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get billing parameter:', error);
      throw new Error(parseErrorMessage(error, 'Failed to get billing parameter'));
    }
  }

  /**
   * Create a new billing parameter - Admin only
   */
  async adminCreateBillingParameter(request: BillingParameterCreateRequest): Promise<BillingParameterDTO> {
    try {
      const response = await axios.post<BillingParameterDTO>(
        `${BILLING_API_BASE}/admin/billing/parameters`,
        request,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to create billing parameter:', error);
      throw new Error(parseErrorMessage(error, 'Failed to create billing parameter'));
    }
  }

  /**
   * Update an existing billing parameter - Admin only
   */
  async adminUpdateBillingParameter(id: number, request: BillingParameterUpdateRequest): Promise<BillingParameterDTO> {
    try {
      const response = await axios.put<BillingParameterDTO>(
        `${BILLING_API_BASE}/admin/billing/parameters/${id}`,
        request,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to update billing parameter:', error);
      throw new Error(parseErrorMessage(error, 'Failed to update billing parameter'));
    }
  }

  /**
   * Delete a billing parameter - Admin only
   */
  async adminDeleteBillingParameter(id: number): Promise<void> {
    try {
      await axios.delete(
        `${BILLING_API_BASE}/admin/billing/parameters/${id}`,
        { timeout: 10000 }
      );
    } catch (error) {
      console.error('Failed to delete billing parameter:', error);
      throw new Error(parseErrorMessage(error, 'Failed to delete billing parameter'));
    }
  }

  /**
   * Get all active FEATURE billing parameters - Admin only
   * Used to populate the "Parent Feature" dropdown in create/edit dialogs.
   */
  async adminGetFeatureBillingParameters(): Promise<BillingParameterDTO[]> {
    try {
      const response = await axios.get<BillingParameterDTO[]>(
        `${BILLING_API_BASE}/admin/billing/parameters/features`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get feature billing parameters:', error);
      throw new Error(parseErrorMessage(error, 'Failed to get feature billing parameters'));
    }
  }

  /**
   * Toggle the active status of a billing parameter - Admin only
   */
  async adminToggleBillingParameterActive(id: number): Promise<BillingParameterDTO> {
    try {
      const response = await axios.post<BillingParameterDTO>(
        `${BILLING_API_BASE}/admin/billing/parameters/${id}/toggle-active`,
        {},
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to toggle billing parameter active status:', error);
      throw new Error(parseErrorMessage(error, 'Failed to toggle billing parameter status'));
    }
  }

  // ========================================
  // Plan Management - Admin only
  // ========================================

  /**
   * Get all plans with pagination - Admin only
   */
  async adminGetPlans(params: {
    page?: number;
    size?: number;
    search?: string;
    status?: PlanStatus;
    paymentProvider?: PaymentProvider;
  } = {}): Promise<PageResponse<PlanSummaryDTO>> {
    try {
      const response = await axios.get<PageResponse<PlanSummaryDTO>>(
        `${BILLING_API_BASE}/admin/billing/plans`,
        {
          params: {
            page: params.page ?? 0,
            size: params.size ?? 20,
            search: params.search,
            status: params.status,
            paymentProvider: params.paymentProvider,
          },
          timeout: 10000,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get plans:', error);
      throw new Error(parseErrorMessage(error, 'Failed to get plans'));
    }
  }

  /**
   * Get plan statistics - Admin only
   */
  async adminGetPlanStats(): Promise<PlanStatsDTO> {
    try {
      const response = await axios.get<PlanStatsDTO>(
        `${BILLING_API_BASE}/admin/billing/plans/stats`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get plan stats:', error);
      throw new Error(parseErrorMessage(error, 'Failed to get plan stats'));
    }
  }

  /**
   * Get a specific plan by ID with full details - Admin only
   */
  async adminGetPlanById(id: number): Promise<PlanDTO> {
    try {
      const response = await axios.get<PlanDTO>(
        `${BILLING_API_BASE}/admin/billing/plans/${id}`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get plan:', error);
      throw new Error(parseErrorMessage(error, 'Failed to get plan'));
    }
  }

  /**
   * Get a specific plan by code - Admin only
   */
  async adminGetPlanByCode(code: string): Promise<PlanDTO> {
    try {
      const response = await axios.get<PlanDTO>(
        `${BILLING_API_BASE}/admin/billing/plans/code/${encodeURIComponent(code)}`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get plan:', error);
      throw new Error(parseErrorMessage(error, 'Failed to get plan'));
    }
  }

  /**
   * Create a new plan - Admin only
   */
  async adminCreatePlan(request: PlanCreateRequest): Promise<PlanDTO> {
    try {
      const response = await axios.post<PlanDTO>(
        `${BILLING_API_BASE}/admin/billing/plans`,
        request,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to create plan:', error);
      throw new Error(parseErrorMessage(error, 'Failed to create plan'));
    }
  }

  /**
   * Update an existing plan - Admin only
   */
  async adminUpdatePlan(id: number, request: PlanUpdateRequest): Promise<PlanDTO> {
    try {
      const response = await axios.put<PlanDTO>(
        `${BILLING_API_BASE}/admin/billing/plans/${id}`,
        request,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to update plan:', error);
      throw new Error(parseErrorMessage(error, 'Failed to update plan'));
    }
  }

  /**
   * Delete a plan - Admin only
   */
  async adminDeletePlan(id: number): Promise<void> {
    try {
      await axios.delete(
        `${BILLING_API_BASE}/admin/billing/plans/${id}`,
        { timeout: 10000 }
      );
    } catch (error) {
      console.error('Failed to delete plan:', error);
      throw new Error(parseErrorMessage(error, 'Failed to delete plan'));
    }
  }

  /**
   * Duplicate a plan - Admin only
   */
  async adminDuplicatePlan(id: number, newCode: string, newName: string): Promise<PlanDTO> {
    try {
      const response = await axios.post<PlanDTO>(
        `${BILLING_API_BASE}/admin/billing/plans/${id}/duplicate`,
        { newCode, newName },
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to duplicate plan:', error);
      throw new Error(parseErrorMessage(error, 'Failed to duplicate plan'));
    }
  }

  /**
   * Update plan status (activate/deactivate/archive) - Admin only
   */
  async adminUpdatePlanStatus(id: number, status: PlanStatus): Promise<PlanDTO> {
    try {
      const response = await axios.post<PlanDTO>(
        `${BILLING_API_BASE}/admin/billing/plans/${id}/status`,
        {},
        { 
          params: { status },
          timeout: 10000 
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to update plan status:', error);
      throw new Error(parseErrorMessage(error, 'Failed to update plan status'));
    }
  }

  /**
   * Add a parameter configuration to a plan - Admin only
   */
  async adminAddPlanParameter(planId: number, request: PlanParameterConfigCreateRequest): Promise<PlanParameterConfigDTO> {
    try {
      const response = await axios.post<PlanParameterConfigDTO>(
        `${BILLING_API_BASE}/admin/billing/plans/${planId}/parameters`,
        request,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to add plan parameter:', error);
      throw new Error(parseErrorMessage(error, 'Failed to add parameter to plan'));
    }
  }

  /**
   * Update a parameter configuration in a plan - Admin only
   */
  async adminUpdatePlanParameter(planId: number, configId: number, request: PlanParameterConfigUpdateRequest): Promise<PlanParameterConfigDTO> {
    try {
      const response = await axios.put<PlanParameterConfigDTO>(
        `${BILLING_API_BASE}/admin/billing/plans/${planId}/parameters/${configId}`,
        request,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to update plan parameter:', error);
      throw new Error(parseErrorMessage(error, 'Failed to update parameter in plan'));
    }
  }

  /**
   * Remove a parameter configuration from a plan - Admin only
   */
  async adminRemovePlanParameter(planId: number, configId: number): Promise<void> {
    try {
      await axios.delete(
        `${BILLING_API_BASE}/admin/billing/plans/${planId}/parameters/${configId}`,
        { timeout: 10000 }
      );
    } catch (error) {
      console.error('Failed to remove plan parameter:', error);
      throw new Error(parseErrorMessage(error, 'Failed to remove parameter from plan'));
    }
  }

  /**
   * Batch update parameter configurations for a plan - Admin only
   */
  async adminBatchUpdatePlanParameters(planId: number, configs: PlanParameterConfigCreateRequest[]): Promise<PlanDTO> {
    try {
      const response = await axios.put<PlanDTO>(
        `${BILLING_API_BASE}/admin/billing/plans/${planId}/parameters`,
        { parameterConfigs: configs },
        { timeout: 15000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to batch update plan parameters:', error);
      throw new Error(parseErrorMessage(error, 'Failed to update plan parameters'));
    }
  }

  /**
   * Sync a plan to Stripe (creates/updates Product and Price) - Admin only
   */
  async adminSyncPlanToStripe(planId: number): Promise<PlanStripeSyncResultDTO> {
    try {
      const response = await axios.post<PlanStripeSyncResultDTO>(
        `${BILLING_API_BASE}/admin/billing/plans/${planId}/sync-stripe`,
        {},
        { timeout: 30000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to sync plan to Stripe:', error);
      throw new Error(parseErrorMessage(error, 'Failed to sync plan to Stripe'));
    }
  }

  /**
   * Get Stripe sync status for a plan - Admin only
   */
  async adminGetPlanStripeStatus(planId: number): Promise<{
    planId: number;
    planCode: string;
    stripeSyncStatus: StripeSyncStatus;
    stripeProductId: string | null;
    stripePriceId: string | null;
    stripePriceVersion: number;
    stripeSyncedAt: string | null;
    stripeSyncError: string | null;
  }> {
    try {
      const response = await axios.get(
        `${BILLING_API_BASE}/admin/billing/plans/${planId}/stripe-status`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get Stripe status:', error);
      throw new Error(parseErrorMessage(error, 'Failed to get Stripe sync status'));
    }
  }

  // ========================================
  // Normalized BillingPlan Management (EF-259) - Admin only
  // BillingPlan = tier (→ Stripe Product)
  // BillingPlanPrice = price option (→ Stripe Price)
  // ========================================

  async adminGetBillingPlans(params: {
    page?: number;
    size?: number;
    search?: string;
    status?: PlanStatus;
  } = {}): Promise<PageResponse<BillingPlanSummaryDTO>> {
    try {
      const response = await axios.get<PageResponse<BillingPlanSummaryDTO>>(
        `${BILLING_API_BASE}/admin/billing-plans`,
        {
          params: { page: params.page ?? 0, size: params.size ?? 20, search: params.search, status: params.status },
          timeout: 10000,
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error, 'Failed to get billing plans'));
    }
  }

  async adminGetBillingPlanStats(): Promise<BillingPlanStatsDTO> {
    try {
      const response = await axios.get<BillingPlanStatsDTO>(
        `${BILLING_API_BASE}/admin/billing-plans/stats`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error, 'Failed to get billing plan stats'));
    }
  }

  async adminGetBillingPlanById(id: number): Promise<BillingPlanDTO> {
    try {
      const response = await axios.get<BillingPlanDTO>(
        `${BILLING_API_BASE}/admin/billing-plans/${id}`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error, 'Failed to get billing plan'));
    }
  }

  async adminCreateBillingPlan(request: BillingPlanCreateRequest): Promise<BillingPlanDTO> {
    try {
      const response = await axios.post<BillingPlanDTO>(
        `${BILLING_API_BASE}/admin/billing-plans`,
        request,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error, 'Failed to create billing plan'));
    }
  }

  async adminUpdateBillingPlan(id: number, request: BillingPlanUpdateRequest): Promise<BillingPlanDTO> {
    try {
      const response = await axios.put<BillingPlanDTO>(
        `${BILLING_API_BASE}/admin/billing-plans/${id}`,
        request,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error, 'Failed to update billing plan'));
    }
  }

  async adminDeleteBillingPlan(id: number): Promise<void> {
    try {
      await axios.delete(`${BILLING_API_BASE}/admin/billing-plans/${id}`, { timeout: 10000 });
    } catch (error) {
      throw new Error(parseErrorMessage(error, 'Failed to delete billing plan'));
    }
  }

  async adminUpdateBillingPlanStatus(id: number, status: PlanStatus): Promise<BillingPlanDTO> {
    try {
      const response = await axios.post<BillingPlanDTO>(
        `${BILLING_API_BASE}/admin/billing-plans/${id}/status`,
        {},
        { params: { status }, timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error, 'Failed to update billing plan status'));
    }
  }

  // Price Options

  async adminAddBillingPlanPrice(planId: number, request: BillingPlanPriceCreateRequest): Promise<BillingPlanPriceDTO> {
    try {
      const response = await axios.post<BillingPlanPriceDTO>(
        `${BILLING_API_BASE}/admin/billing-plans/${planId}/prices`,
        request,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error, 'Failed to add price option'));
    }
  }

  async adminUpdateBillingPlanPrice(planId: number, priceId: number, request: BillingPlanPriceUpdateRequest): Promise<BillingPlanPriceDTO> {
    try {
      const response = await axios.put<BillingPlanPriceDTO>(
        `${BILLING_API_BASE}/admin/billing-plans/${planId}/prices/${priceId}`,
        request,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error, 'Failed to update price option'));
    }
  }

  async adminDeleteBillingPlanPrice(planId: number, priceId: number): Promise<void> {
    try {
      await axios.delete(
        `${BILLING_API_BASE}/admin/billing-plans/${planId}/prices/${priceId}`,
        { timeout: 10000 }
      );
    } catch (error) {
      throw new Error(parseErrorMessage(error, 'Failed to delete price option'));
    }
  }

  async adminSyncBillingPlanPriceToStripe(planId: number, priceId: number): Promise<BillingPlanStripeSyncResultDTO> {
    try {
      const response = await axios.post<BillingPlanStripeSyncResultDTO>(
        `${BILLING_API_BASE}/admin/billing-plans/${planId}/prices/${priceId}/sync-stripe`,
        {},
        { timeout: 30000 }
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error, 'Failed to sync price option to Stripe'));
    }
  }

  // Parameter configs (tier-level, shared across all price options)

  async adminAddBillingPlanParameter(planId: number, request: PlanParameterConfigCreateRequest): Promise<BillingPlanParameterConfigDTO> {
    try {
      const response = await axios.post<BillingPlanParameterConfigDTO>(
        `${BILLING_API_BASE}/admin/billing-plans/${planId}/parameters`,
        request,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error, 'Failed to add parameter to billing plan'));
    }
  }

  async adminUpdateBillingPlanParameter(planId: number, configId: number, request: PlanParameterConfigUpdateRequest): Promise<BillingPlanParameterConfigDTO> {
    try {
      const response = await axios.put<BillingPlanParameterConfigDTO>(
        `${BILLING_API_BASE}/admin/billing-plans/${planId}/parameters/${configId}`,
        request,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error, 'Failed to update parameter in billing plan'));
    }
  }

  async adminRemoveBillingPlanParameter(planId: number, configId: number): Promise<void> {
    try {
      await axios.delete(
        `${BILLING_API_BASE}/admin/billing-plans/${planId}/parameters/${configId}`,
        { timeout: 10000 }
      );
    } catch (error) {
      throw new Error(parseErrorMessage(error, 'Failed to remove parameter from billing plan'));
    }
  }

  async adminBatchUpdateBillingPlanParameters(planId: number, configs: PlanParameterConfigCreateRequest[]): Promise<BillingPlanDTO> {
    try {
      const response = await axios.put<BillingPlanDTO>(
        `${BILLING_API_BASE}/admin/billing-plans/${planId}/parameters`,
        { parameterConfigs: configs },
        { timeout: 15000 }
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error, 'Failed to update billing plan parameters'));
    }
  }

  // ========================================
  // Subscription Management - Tenant Admins/Owners
  // ========================================

  /**
   * Get the current subscription for a tenant.
   * Returns null if no subscription exists.
   * @param tenantId - The tenant ID
   */
  async getSubscription(tenantId: string): Promise<SubscriptionDTO | null> {
    try {
      const response = await axios.get<SubscriptionDTO>(
        `${BILLING_API_BASE}/billing/tenants/${encodeURIComponent(tenantId)}/subscription`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null; // No subscription found
      }
      console.error('Failed to get subscription:', error);
      throw new Error(parseErrorMessage(error, 'Failed to get subscription'));
    }
  }

  /**
   * Get subscription history for a tenant.
   * @param tenantId - The tenant ID
   */
  async getSubscriptionHistory(tenantId: string): Promise<SubscriptionDTO[]> {
    try {
      const response = await axios.get<SubscriptionDTO[]>(
        `${BILLING_API_BASE}/billing/tenants/${encodeURIComponent(tenantId)}/subscriptions`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get subscription history:', error);
      throw new Error(parseErrorMessage(error, 'Failed to get subscription history'));
    }
  }

  /**
   * Create a checkout session for subscription purchase.
   * Returns URL to redirect user to Stripe Checkout.
   * @param tenantId - The tenant ID
   * @param request - Checkout request with plan ID and URLs
   */
  async createSubscriptionCheckout(
    tenantId: string, 
    request: CheckoutSessionRequest
  ): Promise<CheckoutSessionResponse> {
    try {
      const response = await axios.post<CheckoutSessionResponse>(
        `${BILLING_API_BASE}/billing/tenants/${encodeURIComponent(tenantId)}/checkout/subscription`,
        request,
        { timeout: 15000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      throw new Error(parseErrorMessage(error, 'Failed to create checkout session'));
    }
  }

  /**
   * Get available plans for subscription (public plans only).
   * For use in plan selection UI.
   * Uses the public billing endpoint, not admin endpoint.
   */
  async getAvailablePlans(): Promise<PlanSummaryDTO[]> {
    try {
      const response = await axios.get<PlanSummaryDTO[]>(
        `${BILLING_API_BASE}/billing/plans/available`,
        {
          timeout: 10000,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get available plans:', error);
      throw new Error(parseErrorMessage(error, 'Failed to get available plans'));
    }
  }

  /**
   * Get available billing plan tiers (new catalog) for subscription.
   * Returns active, public tiers ordered by rank, each with active price options.
   * Use this for the tenant-facing plan selection UI (replaces getAvailablePlans).
   */
  async getAvailableBillingPlans(): Promise<BillingPlanDTO[]> {
    try {
      const response = await axios.get<BillingPlanDTO[]>(
        `${BILLING_API_BASE}/billing/billing-plans/available`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get available billing plans:', error);
      throw new Error(parseErrorMessage(error, 'Failed to get available billing plans'));
    }
  }

  /**
   * Cancel the current subscription for a tenant.
   * By default cancels at period end. Set cancelImmediately for immediate cancellation.
   * @param tenantId - The tenant ID
   * @param cancelImmediately - Whether to cancel immediately (default: false = cancel at period end)
   */
  async cancelSubscription(tenantId: string, cancelImmediately = false): Promise<SubscriptionDTO> {
    try {
      const response = await axios.post<SubscriptionDTO>(
        `${BILLING_API_BASE}/billing/tenants/${encodeURIComponent(tenantId)}/subscription/cancel`,
        { cancelImmediately },
        { timeout: 15000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      throw new Error(parseErrorMessage(error, 'Failed to cancel subscription'));
    }
  }

  /**
   * Reactivate a subscription that was scheduled for cancellation.
   * @param tenantId - The tenant ID
   */
  async reactivateSubscription(tenantId: string): Promise<SubscriptionDTO> {
    try {
      const response = await axios.post<SubscriptionDTO>(
        `${BILLING_API_BASE}/billing/tenants/${encodeURIComponent(tenantId)}/subscription/reactivate`,
        {},
        { timeout: 15000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to reactivate subscription:', error);
      throw new Error(parseErrorMessage(error, 'Failed to reactivate subscription'));
    }
  }

  /**
   * Activate the default free plan for a tenant with no active subscription.
   * Used when the automatic assignment on signup failed.
   * @param tenantId - The tenant ID
   */
  async activateFreePlan(tenantId: string): Promise<SubscriptionDTO> {
    try {
      const response = await axios.post<SubscriptionDTO>(
        `${BILLING_API_BASE}/billing/tenants/${encodeURIComponent(tenantId)}/subscription/activate-free-plan`,
        {},
        { timeout: 15000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to activate free plan:', error);
      throw new Error(parseErrorMessage(error, 'Failed to activate free plan'));
    }
  }

  /**
   * Cancel a scheduled end-of-period downgrade.
   * @param tenantId - The tenant ID
   */
  async cancelScheduledDowngrade(tenantId: string): Promise<SubscriptionDTO> {
    try {
      const response = await axios.delete<SubscriptionDTO>(
        `${BILLING_API_BASE}/billing/tenants/${encodeURIComponent(tenantId)}/subscription/scheduled-change`,
        { timeout: 15000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to cancel scheduled downgrade:', error);
      throw new Error(parseErrorMessage(error, 'Failed to cancel scheduled downgrade'));
    }
  }

  /**
   * Upgrade or downgrade the current subscription to a different plan.
   * Upgrades take effect immediately with proration; downgrades default to end-of-period.
   * @param tenantId - The tenant ID
   * @param request - newPlanId and optional prorationBehavior override
   */
  async updateSubscription(
    tenantId: string,
    request: SubscriptionUpdateRequest
  ): Promise<SubscriptionUpdateResponse> {
    try {
      const response = await axios.put<SubscriptionUpdateResponse>(
        `${BILLING_API_BASE}/billing/tenants/${encodeURIComponent(tenantId)}/subscription`,
        request,
        { timeout: 20000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to update subscription:', error);
      throw new Error(parseErrorMessage(error, 'Failed to update subscription'));
    }
  }

  // ========================================
  // Customer Portal
  // ========================================

  /**
   * Create a Stripe Customer Portal session.
   * Returns a short-lived URL to Stripe's hosted portal.
   * @param tenantId - The tenant ID
   * @param returnUrl - URL to redirect back to after leaving the portal
   */
  async createPortalSession(tenantId: string, returnUrl: string): Promise<CustomerPortalSessionDTO> {
    try {
      const response = await axios.post<CustomerPortalSessionDTO>(
        `${BILLING_API_BASE}/billing/tenants/${encodeURIComponent(tenantId)}/portal`,
        null,
        { params: { returnUrl }, timeout: 15000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to create portal session:', error);
      throw new Error(parseErrorMessage(error, 'Failed to open billing portal'));
    }
  }

  // ========================================
  // Payment Methods
  // ========================================

  /**
   * List payment methods for a tenant.
   * @param tenantId - The tenant ID
   */
  async listPaymentMethods(tenantId: string): Promise<PaymentMethodDTO[]> {
    try {
      const response = await axios.get<PaymentMethodDTO[]>(
        `${BILLING_API_BASE}/billing/tenants/${encodeURIComponent(tenantId)}/payment-methods`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to list payment methods:', error);
      throw new Error(parseErrorMessage(error, 'Failed to load payment methods'));
    }
  }

  /**
   * Set a payment method as the default.
   * @param tenantId - The tenant ID
   * @param paymentMethodId - The Stripe payment method ID
   */
  async setDefaultPaymentMethod(tenantId: string, paymentMethodId: string): Promise<void> {
    try {
      await axios.post(
        `${BILLING_API_BASE}/billing/tenants/${encodeURIComponent(tenantId)}/payment-methods/${encodeURIComponent(paymentMethodId)}/default`,
        null,
        { timeout: 10000 }
      );
    } catch (error) {
      console.error('Failed to set default payment method:', error);
      throw new Error(parseErrorMessage(error, 'Failed to set default payment method'));
    }
  }

  /**
   * Remove a payment method from a tenant's account.
   * @param tenantId - The tenant ID
   * @param paymentMethodId - The Stripe payment method ID to remove
   */
  async removePaymentMethod(tenantId: string, paymentMethodId: string): Promise<void> {
    try {
      await axios.delete(
        `${BILLING_API_BASE}/billing/tenants/${encodeURIComponent(tenantId)}/payment-methods/${encodeURIComponent(paymentMethodId)}`,
        { timeout: 10000 }
      );
    } catch (error) {
      console.error('Failed to remove payment method:', error);
      throw new Error(parseErrorMessage(error, 'Failed to remove payment method'));
    }
  }

  // ========================================
  // Invoices
  // ========================================

  /**
   * List invoices for a tenant (paginated).
   * @param tenantId - The tenant ID
   * @param page - Page number (0-indexed)
   * @param size - Page size
   */
  async listInvoices(tenantId: string, page = 0, size = 20): Promise<PagedResponse<InvoiceDTO>> {
    try {
      const response = await axios.get<PagedResponse<InvoiceDTO>>(
        `${BILLING_API_BASE}/billing/tenants/${encodeURIComponent(tenantId)}/invoices`,
        { params: { page, size }, timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to list invoices:', error);
      throw new Error(parseErrorMessage(error, 'Failed to load invoices'));
    }
  }

  /**
   * Get a single invoice by ID.
   * @param tenantId - The tenant ID
   * @param invoiceId - The invoice ID
   */
  async getInvoice(tenantId: string, invoiceId: number): Promise<InvoiceDTO> {
    try {
      const response = await axios.get<InvoiceDTO>(
        `${BILLING_API_BASE}/billing/tenants/${encodeURIComponent(tenantId)}/invoices/${invoiceId}`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get invoice:', error);
      throw new Error(parseErrorMessage(error, 'Failed to load invoice'));
    }
  }

  // ========================================
  // Usage Analytics - Tenant Admins/Owners
  // ========================================

  /**
   * Get current-period usage summary including plan limits and credit balance.
   * @param tenantId - The tenant ID
   */
  async getUsageSummary(tenantId: string): Promise<UsageSummaryDTO> {
    try {
      const response = await axios.get<UsageSummaryDTO>(
        `${BILLING_API_BASE}/billing/tenants/${encodeURIComponent(tenantId)}/usage/summary`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get usage summary:', error);
      throw new Error(parseErrorMessage(error, 'Failed to load usage summary'));
    }
  }

  /**
   * Get daily usage history for charting.
   * @param tenantId - The tenant ID
   * @param days - Look-back window: 7, 30 (default), or 90
   */
  async getUsageHistory(tenantId: string, days: 7 | 30 | 90 = 30): Promise<UsageHistoryDTO> {
    try {
      const response = await axios.get<UsageHistoryDTO>(
        `${BILLING_API_BASE}/billing/tenants/${encodeURIComponent(tenantId)}/usage/history`,
        { params: { days }, timeout: 15000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get usage history:', error);
      throw new Error(parseErrorMessage(error, 'Failed to load usage history'));
    }
  }

  // ========================================
  // Admin Subscription Management (Platform Admin)
  // ========================================

  /**
   * Get all subscriptions across tenants (paginated).
   */
  async adminGetAllSubscriptions(
    params: { page?: number; size?: number; status?: string; tenantId?: string } = {}
  ): Promise<PageResponse<SubscriptionDTO>> {
    try {
      const { page = 0, size = 20, status, tenantId } = params;
      const queryParams: Record<string, string | number> = { page, size };
      if (status && status !== 'all') queryParams.status = status;
      if (tenantId) queryParams.tenantId = tenantId;
      const response = await axios.get<PageResponse<SubscriptionDTO>>(
        `${BILLING_API_BASE}/admin/billing/subscriptions`,
        { params: queryParams, timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get admin subscriptions:', error);
      throw new Error(parseErrorMessage(error, 'Failed to load subscriptions'));
    }
  }

  /**
   * Get subscription statistics for platform admin dashboard.
   */
  async adminGetSubscriptionStats(): Promise<SubscriptionStatsDTO> {
    try {
      const response = await axios.get<SubscriptionStatsDTO>(
        `${BILLING_API_BASE}/admin/billing/subscriptions/stats`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get subscription stats:', error);
      throw new Error(parseErrorMessage(error, 'Failed to load subscription stats'));
    }
  }

  /**
   * Get a single subscription by ID (admin).
   */
  async adminGetSubscriptionById(id: number): Promise<SubscriptionDTO> {
    try {
      const response = await axios.get<SubscriptionDTO>(
        `${BILLING_API_BASE}/admin/billing/subscriptions/${id}`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get subscription:', error);
      throw new Error(parseErrorMessage(error, 'Failed to load subscription'));
    }
  }

  /**
   * Update a subscription status (admin override).
   */
  async adminUpdateSubscriptionStatus(id: number, status: string): Promise<SubscriptionDTO> {
    try {
      const response = await axios.put<SubscriptionDTO>(
        `${BILLING_API_BASE}/admin/billing/subscriptions/${id}/status`,
        { status },
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to update subscription status:', error);
      throw new Error(parseErrorMessage(error, 'Failed to update subscription status'));
    }
  }

  /**
   * Change subscription plan (admin-initiated). Allows overriding proration behavior.
   * @param subscriptionId - The subscription ID
   * @param request - newPlanId and optional prorationBehavior
   */
  async adminChangeSubscriptionPlan(
    subscriptionId: number,
    request: SubscriptionUpdateRequest
  ): Promise<SubscriptionUpdateResponse> {
    try {
      const response = await axios.post<SubscriptionUpdateResponse>(
        `${BILLING_API_BASE}/admin/billing/subscriptions/${subscriptionId}/change-plan`,
        request,
        { timeout: 20000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to change subscription plan:', error);
      throw new Error(parseErrorMessage(error, 'Failed to change subscription plan'));
    }
  }

  // ========================================
  // Admin Invoice Management (Platform Admin)
  // ========================================

  /**
   * Get all invoices across tenants (paginated).
   */
  async adminGetAllInvoices(
    params: { page?: number; size?: number; status?: string; tenantId?: string } = {}
  ): Promise<PageResponse<InvoiceDTO>> {
    try {
      const { page = 0, size = 20, status, tenantId } = params;
      const queryParams: Record<string, string | number> = { page, size };
      if (status && status !== 'all') queryParams.status = status;
      if (tenantId) queryParams.tenantId = tenantId;
      const response = await axios.get<PageResponse<InvoiceDTO>>(
        `${BILLING_API_BASE}/admin/billing/invoices`,
        { params: queryParams, timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get admin invoices:', error);
      throw new Error(parseErrorMessage(error, 'Failed to load invoices'));
    }
  }

  /**
   * Get invoice statistics for platform admin dashboard.
   */
  async adminGetInvoiceStats(): Promise<InvoiceStatsDTO> {
    try {
      const response = await axios.get<InvoiceStatsDTO>(
        `${BILLING_API_BASE}/admin/billing/invoices/stats`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get invoice stats:', error);
      throw new Error(parseErrorMessage(error, 'Failed to load invoice stats'));
    }
  }

  /**
   * Get a single invoice by ID (admin).
   */
  async adminGetInvoiceById(id: number): Promise<InvoiceDTO> {
    try {
      const response = await axios.get<InvoiceDTO>(
        `${BILLING_API_BASE}/admin/billing/invoices/${id}`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get invoice:', error);
      throw new Error(parseErrorMessage(error, 'Failed to load invoice'));
    }
  }

  /**
   * Download usage report as CSV.
   * Triggers a browser download by creating a temporary anchor element.
   * @param tenantId - The tenant ID
   * @param days - Look-back window: 7, 30 (default), or 90
   */
  async exportUsageCsv(tenantId: string, days: 7 | 30 | 90 = 30): Promise<void> {
    try {
      const response = await axios.get(
        `${BILLING_API_BASE}/billing/tenants/${encodeURIComponent(tenantId)}/usage/export`,
        { params: { days }, responseType: 'blob', timeout: 20000 }
      );
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `usage-report-${days}d.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export usage CSV:', error);
      throw new Error(parseErrorMessage(error, 'Failed to export usage report'));
    }
  }

  // ========================================
  // Signup Bonus Config - Public
  // ========================================

  /**
   * Get the current platform-wide signup bonus configuration.
   * Accessible to all authenticated users (used on the onboarding success screen).
   */
  async getSignupBonusConfig(): Promise<SignupBonusConfigDTO> {
    try {
      const response = await axios.get<SignupBonusConfigDTO>(
        `${BILLING_API_BASE}/billing/signup-bonus/config`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get signup bonus config:', error);
      throw new Error(parseErrorMessage(error, 'Failed to load signup bonus config'));
    }
  }

  // ========================================
  // Signup Bonus Config - Admin only
  // ========================================

  /**
   * Get the current platform-wide signup bonus configuration.
   */
  async adminGetSignupBonusConfig(): Promise<SignupBonusConfigDTO> {
    try {
      const response = await axios.get<SignupBonusConfigDTO>(
        `${BILLING_API_BASE}/admin/billing/signup-bonus/config`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get signup bonus config:', error);
      throw new Error(parseErrorMessage(error, 'Failed to load signup bonus config'));
    }
  }

  /**
   * Update the platform-wide signup bonus configuration.
   */
  async adminUpdateSignupBonusConfig(config: SignupBonusConfigDTO): Promise<SignupBonusConfigDTO> {
    try {
      const response = await axios.put<SignupBonusConfigDTO>(
        `${BILLING_API_BASE}/admin/billing/signup-bonus/config`,
        config,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to update signup bonus config:', error);
      throw new Error(parseErrorMessage(error, 'Failed to update signup bonus config'));
    }
  }

  // ========================================
  // Billing Profile (EF-227)
  // ========================================

  async getBillingProfile(tenantId: string): Promise<BillingProfileDTO> {
    try {
      const response = await axios.get<BillingProfileDTO>(
        `${BILLING_API_BASE}/billing/tenants/${encodeURIComponent(tenantId)}/billing-profile`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error, 'Failed to load billing profile'));
    }
  }

  async updateBillingProfile(tenantId: string, profile: BillingProfileDTO): Promise<BillingProfileDTO> {
    try {
      const response = await axios.put<BillingProfileDTO>(
        `${BILLING_API_BASE}/billing/tenants/${encodeURIComponent(tenantId)}/billing-profile`,
        profile,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error, 'Failed to update billing profile'));
    }
  }

  // Admin variants — uses /admin/billing/tenants/{id}/billing-profile
  async adminGetBillingProfile(tenantId: string): Promise<BillingProfileDTO> {
    try {
      const response = await axios.get<BillingProfileDTO>(
        `${BILLING_API_BASE}/admin/billing/tenants/${encodeURIComponent(tenantId)}/billing-profile`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error, 'Failed to load billing profile'));
    }
  }

  async adminUpdateBillingProfile(tenantId: string, profile: BillingProfileDTO): Promise<BillingProfileDTO> {
    try {
      const response = await axios.put<BillingProfileDTO>(
        `${BILLING_API_BASE}/admin/billing/tenants/${encodeURIComponent(tenantId)}/billing-profile`,
        profile,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error, 'Failed to update billing profile'));
    }
  }
}

export const billingApi = new BillingApiService();
export default billingApi;

// ========================================
// Billing Profile Types (EF-227)
// ========================================

export interface BillingAddressDTO {
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
}

export interface BillingProfileDTO {
  billingContactName: string | null;
  billingEmail: string | null;
  billingPhone: string | null;
  companyLegalName: string | null;
  taxId: string | null;
  billingAddress: BillingAddressDTO | null;
}

// ========================================
// Signup Bonus Config
// ========================================
export interface SignupBonusConfigDTO {
  enabled: boolean;
  credits: number;
  validityDays: number;
  meteringKey: string;
}

// ========================================
// Promo Code Types (EF-208)
// ========================================

export interface PromoCodeDTO {
  id: number;
  code: string;
  credits: number;
  meteringKey: string;
  validityDays: number;
  maxRedemptions: number | null;
  currentRedemptions: number;
  expiresAt: string | null;
  isActive: boolean;
  description: string | null;
  createdAt: string | null;
  lastUpdated: string | null;
}

export interface PromoCodeValidationResult {
  valid: boolean;
  code: string;
  credits: number | null;
  meteringKey: string | null;
  validityDays: number | null;
  message: string;
}

export interface PromoCodeCreateRequest {
  code: string;
  credits: number;
  meteringKey?: string;
  validityDays?: number;
  maxRedemptions?: number | null;
  expiresAt?: string | null;
  description?: string | null;
}

export interface PromoCodeUpdateRequest {
  maxRedemptions?: number | null;
  expiresAt?: string | null;
  isActive?: boolean;
  description?: string | null;
}

// ========================================
// Promo Code API Service (EF-208)
// ========================================

class PromoCodeApiService {
  private readonly base = API_ENDPOINTS.BILLING;

  async validate(code: string, tenantId?: string): Promise<PromoCodeValidationResult> {
    const response = await axios.get<PromoCodeValidationResult>(
      `${this.base}/promo-codes/${encodeURIComponent(code)}/validate`,
      { params: tenantId ? { tenantId } : {}, timeout: 8000 }
    );
    return response.data;
  }

  async redeem(code: string, tenantId: string): Promise<unknown> {
    const response = await axios.post(
      `${this.base}/promo-codes/${encodeURIComponent(code)}/redeem`,
      null,
      { params: { tenantId }, timeout: 10000 }
    );
    return response.data;
  }

  // Admin

  async adminList(page = 0, size = 20): Promise<PagedResponse<PromoCodeDTO>> {
    const response = await axios.get<PagedResponse<PromoCodeDTO>>(
      `${this.base}/admin/promo-codes`,
      { params: { page, size }, timeout: 10000 }
    );
    return response.data;
  }

  async adminGet(id: number): Promise<PromoCodeDTO> {
    const response = await axios.get<PromoCodeDTO>(
      `${this.base}/admin/promo-codes/${id}`,
      { timeout: 8000 }
    );
    return response.data;
  }

  async adminCreate(request: PromoCodeCreateRequest): Promise<PromoCodeDTO> {
    const response = await axios.post<PromoCodeDTO>(
      `${this.base}/admin/promo-codes`,
      request,
      { timeout: 10000 }
    );
    return response.data;
  }

  async adminUpdate(id: number, request: PromoCodeUpdateRequest): Promise<PromoCodeDTO> {
    const response = await axios.patch<PromoCodeDTO>(
      `${this.base}/admin/promo-codes/${id}`,
      request,
      { timeout: 10000 }
    );
    return response.data;
  }

  async adminDelete(id: number): Promise<void> {
    await axios.delete(`${this.base}/admin/promo-codes/${id}`, { timeout: 10000 });
  }
}

export const promoCodeApi = new PromoCodeApiService();

// ========================================
// Referral / Affiliate Program Types (EF-209)
// ========================================

export interface ReferralCodeDTO {
  id: number;
  tenantId: string;
  code: string;
  shareUrl: string;
  creditsForReferrer: number;
  creditsForReferee: number;
  totalReferrals: number;
  isActive: boolean;
  createdAt: string | null;
}

export interface ReferralCodeUpdateRequest {
  creditsForReferrer?: number | null;
  creditsForReferee?: number | null;
  isActive?: boolean | null;
}

export interface ReferralDefaultsDTO {
  creditsForReferrer: number;
  creditsForReferee: number;
}

export interface ReferralStatsDTO {
  code: string | null;
  shareUrl: string | null;
  totalReferrals: number;
  totalCreditsEarned: number;
  creditsPerReferral: number;
}

export interface ReferralConversionDTO {
  id: number;
  refereeTenantId: string;
  referrerCreditGrantId: number | null;
  refereeCreditGrantId: number | null;
  convertedAt: string;
  status: 'PENDING' | 'CREDITED' | 'REVERSED';
  creditsEarned: number | null;
}

// ========================================
// Referral API Service (EF-209)
// ========================================

class ReferralApiService {
  private readonly base = API_ENDPOINTS.BILLING;

  /** Get (or auto-create) the tenant's referral code. */
  async getMyCode(tenantId: string): Promise<ReferralCodeDTO> {
    const response = await axios.get<ReferralCodeDTO>(
      `${this.base}/referrals/tenants/${encodeURIComponent(tenantId)}/my-code`,
      { timeout: 10000 }
    );
    return response.data;
  }

  /** Get referral stats for the tenant. */
  async getStats(tenantId: string): Promise<ReferralStatsDTO> {
    const response = await axios.get<ReferralStatsDTO>(
      `${this.base}/referrals/tenants/${encodeURIComponent(tenantId)}/stats`,
      { timeout: 10000 }
    );
    return response.data;
  }

  /** Get paginated conversion history for the tenant. */
  async getConversions(tenantId: string, page = 0, size = 20): Promise<PagedResponse<ReferralConversionDTO>> {
    const response = await axios.get<PagedResponse<ReferralConversionDTO>>(
      `${this.base}/referrals/tenants/${encodeURIComponent(tenantId)}/conversions`,
      { params: { page, size }, timeout: 10000 }
    );
    return response.data;
  }

  /**
   * Attribute a referral code to the tenant after onboarding.
   * Non-fatal — the caller should swallow errors gracefully.
   */
  async processReferral(tenantId: string, code: string): Promise<void> {
    await axios.post(
      `${this.base}/referrals/tenants/${encodeURIComponent(tenantId)}/process`,
      null,
      { params: { code }, timeout: 10000 }
    );
  }

  // Admin

  async adminList(page = 0, size = 20): Promise<PagedResponse<ReferralCodeDTO>> {
    const response = await axios.get<PagedResponse<ReferralCodeDTO>>(
      `${this.base}/admin/referrals`,
      { params: { page, size }, timeout: 10000 }
    );
    return response.data;
  }

  async adminUpdate(id: number, request: ReferralCodeUpdateRequest): Promise<ReferralCodeDTO> {
    const response = await axios.patch<ReferralCodeDTO>(
      `${this.base}/admin/referrals/${id}`,
      request,
      { timeout: 10000 }
    );
    return response.data;
  }

  async adminDeactivate(id: number): Promise<ReferralCodeDTO> {
    const response = await axios.post<ReferralCodeDTO>(
      `${this.base}/admin/referrals/${id}/deactivate`,
      null,
      { timeout: 10000 }
    );
    return response.data;
  }

  async adminActivate(id: number): Promise<ReferralCodeDTO> {
    const response = await axios.post<ReferralCodeDTO>(
      `${this.base}/admin/referrals/${id}/activate`,
      null,
      { timeout: 10000 }
    );
    return response.data;
  }

  async adminGetDefaults(): Promise<ReferralDefaultsDTO> {
    const response = await axios.get<ReferralDefaultsDTO>(
      `${this.base}/admin/referrals/defaults`,
      { timeout: 10000 }
    );
    return response.data;
  }

  async adminUpdateDefaults(request: ReferralDefaultsDTO): Promise<ReferralDefaultsDTO> {
    const response = await axios.put<ReferralDefaultsDTO>(
      `${this.base}/admin/referrals/defaults`,
      request,
      { timeout: 10000 }
    );
    return response.data;
  }
}

export const referralApi = new ReferralApiService();
